import { SignupBotSignals } from 'src/app/core/util/signup-form/signup-form';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpBackendService } from 'src/app/core/backend-services/http/http.backend-service';
import { UserInfoInterface, SessionResponseInterface } from '../../interfaces';

/** Filter keys of the game history page (backend: user/detailed-dashboard-stats). */
export type HistoryStatsGameType = 'all' | '2_player' | '3_player' | '4_player' | 'training';
export type HistoryStatsPeriod = 'all_time' | 'today' | 'this_week' | 'this_month' | 'this_year';

/**
 * Detail statistics over ALL games matching the filters. Opponents never include the bot
 * or the own partner in 4-player games. `null` = no data (e.g. no game with a known duration).
 */
export interface DetailedDashboardStats {
  gameSuccesses: {
    longestWinStreak: number;
    fastestWinSeconds: number | null;
    fastestWinMinutes: number | null;
  };
  opponentStats: {
    mostPlayedOpponent: {
      userID: number;
      username: string | null;
      gameCount: number;
    } | null;
    bestWinRateAgainst: {
      userID: number;
      username: string | null;
      winRate: number;
      gameCount: number;
    } | null;
    uniqueOpponents: number;
  };
  timeStats: {
    mostActiveHour: string | null;
    favoriteWeekday: string | null;
    totalPlaytimeMinutes: number | null;
    totalPlaytimeHours: number | null;
    /** games whose real duration is known (older games may have none) */
    gamesWithDuration: number;
    gamesTotal: number;
  };
  filters: {
    gameType: HistoryStatsGameType;
    period: HistoryStatsPeriod;
  };
}

export interface UserSearchResult {
  id: number;
  username: string;
  profilePictureURL?: string;
  about?: string;
  status?: string;
  stats: {
    wonGames: number;
    lostGames: number;
  };
  friendshipStatus: 'none' | 'friends' | 'sent' | 'received';
}

export interface UserSearchResponse {
  users: UserSearchResult[];
  query: string;
  total: number;
  hasMore: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserBackendService {
  private readonly BASE_ADDRESS = '/user';

  /**
   * @description constructor of UserBackendService
   * @param {HttpBackendService} http - http backend service
   */
  constructor(private http: HttpBackendService) {}

  /**
   * @description gets online player list
   * @returns {Promise<any[]>}
   */
  public getCurrentlyOnlinePlayers(): Promise<UserInfoInterface[]> {
    return firstValueFrom(
      this.http.get<UserInfoInterface[]>(
        `${this.BASE_ADDRESS}/currently-online`
      )
    );
  }

  /**
   * @description gets user online info
   * @returns {Promise<{isOnline:boolean,isInactive:boolean}>}
   */
  public getOnlineInfo(
    userID: number
  ): Promise<{
    isOnline: boolean;
    isInactive: boolean;
    isVeryInactive?: boolean;
  }> {
    return firstValueFrom(
      this.http.get<{ isOnline: boolean; isInactive: boolean }>(
        `${this.BASE_ADDRESS}/${userID}/online-info`
      )
    );
  }

  /**
   * @description gets user info
   * @returns {Promise<UserInfoInterface>}
   */
  public getPlayerInfo(
    userID: number | undefined = undefined
  ): Promise<UserInfoInterface> {
    return firstValueFrom(
      this.http.get<any>(
        userID ? `${this.BASE_ADDRESS}/${userID}` : `${this.BASE_ADDRESS}`
      )
    );
  }

  /**
   * @description gets my user settings and details
   * @returns {Promise<any>}
   */
  public getSettings(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.BASE_ADDRESS}/settings`));
  }

  /**
   * @description updates user settings
   * @param {any} settings - the settings to update
   * @returns {Promise<any>}
   */
  public updateSettings(settings: any): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/settings`, { settings })
    );
  }

  /**
   * @description registers user
   * @param {string} usernamex
   * @param {string} password
   * @param {string} email
   * @returns {Promise<any>}
   */
  public register(
    username: string,
    password: string,
    email: string,
    botSignals?: SignupBotSignals
  ): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/signup`, {
        username,
        password,
        email,
        ...botSignals,
      })
    );
  }

  /**
   * @description user pw forgotten
   * @param {string} email
   * @returns {Promise<any>}
   */
  public passwordForgotten(email: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/password-forgotten`, {
        email,
      })
    );
  }

  /**
   * @description user change pw
   * @param {string} oldPassword
   * @param {string} newPassword
   * @returns {Promise<any>}
   */
  public changePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/change-password`, {
        oldPassword,
        newPassword,
      })
    );
  }

  /**
   * @description user change username
   * @param {string} newUsername
   * @returns {Promise<any>}
   */
  public changeUsername(newUsername: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/change-username`, {
        username: newUsername,
      })
    );
  }

  /**
   * @description user pw reset
   * @param {string} token
   * @param {string} password
   * @returns {Promise<any>}
   */
  public resetPassword(token: string, password: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/password-reset`, {
        token,
        newPassword: password,
      })
    );
  }

  /**
   * @description user email confirm
   * @param {string} token
   * @returns {Promise<any>}
   */
  public confirmEmail(token: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/confirm-email`, {
        token,
      })
    );
  }

  /**
   * @description update user about text
   * @param {string} about
   * @returns {Promise<any>}
   */
  public updateAbout(about: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/about`, {
        about,
      })
    );
  }

  /**
   * @description set user status
   * @param {string} status
   * @param {string} emoji
   * @returns {Promise<any>}
   */
  public setStatus(status: string, emoji: string): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(`${this.BASE_ADDRESS}/status`, {
        status,
        emoji,
      })
    );
  }

  /**
   * @description get user status (own status)
   * @returns {Promise<any>}
   */
  public getStatus(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.BASE_ADDRESS}/status`));
  }

  /**
   * @description get user status by userID
   * @param {number} userID
   * @returns {Promise<any>}
   */
  public getUserStatus(userID: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.BASE_ADDRESS}/${userID}/status`)
    );
  }

  /**
   * @description get detailed dashboard statistics
   * @returns {Promise<DetailedDashboardStats>}
   */
  public getDetailedDashboardStats(
    gameType: HistoryStatsGameType = 'all',
    period: HistoryStatsPeriod = 'all_time'
  ): Promise<DetailedDashboardStats> {
    const params = new URLSearchParams({ gameType, period });
    return firstValueFrom(
      this.http.get<DetailedDashboardStats>(
        `${this.BASE_ADDRESS}/detailed-dashboard-stats?${params.toString()}`
      )
    );
  }

  /**
   * @description search users by username
   * @param {string} query - search query (min 2 characters)
   * @param {number} limit - max number of results (1-50, default: 20)
   * @param {boolean} excludeFriends - exclude already befriended users (default: false)
   * @returns {Promise<UserSearchResponse>}
   */
  public searchUsers(
    query: string,
    limit: number = 20,
    excludeFriends: boolean = false
  ): Promise<UserSearchResponse> {
    const params = new URLSearchParams({
      query,
      limit: limit.toString(),
      excludeFriends: excludeFriends.toString(),
    });

    return firstValueFrom(
      this.http.get<UserSearchResponse>(`${this.BASE_ADDRESS}/search?${params}`)
    );
  }

  /**
   * @description get user's sessions
   * @returns {Promise<SessionResponseInterface>}
   */
  public getSessions(): Promise<SessionResponseInterface> {
    return firstValueFrom(
      this.http.get<SessionResponseInterface>(`${this.BASE_ADDRESS}/sessions`)
    );
  }

  /**
   * @description logout specific session
   * @param {string} sessionId - session ID to logout
   * @returns {Promise<any>}
   */
  public logoutSession(sessionId: string): Promise<any> {
    return firstValueFrom(
      this.http.delete<any>(`${this.BASE_ADDRESS}/sessions/${sessionId}`)
    );
  }

  /**
   * @description logout all other sessions (keep current session)
   * @returns {Promise<any>}
   */
  public logoutAllOtherSessions(): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/logout-all-other-sessions`, {})
    );
  }
}
